import { motion } from 'framer-motion';
import {
  Home, Grid3x3, Receipt, ClipboardList, Bell as BellIcon,
  Users2, FolderOpen, FileText as FileIcon,
  ChevronRight, ChevronDown, ChevronsLeft, Bell, Globe, Settings, AlertCircle,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Brand mark                                                        */
/* ------------------------------------------------------------------ */
function ShafafiyaLogo({ size = 'md' }) {
  const h = size === 'sm' ? 'h-9' : 'h-10';
  return (
    <img
      src="/assets/shafafiya-logo.png"
      alt="Shafafiya — Health Insurance Exchange"
      className={`${h} w-auto object-contain`}
      style={{ maxWidth: 180 }}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  V2 Sidebar — matches refs 9, 11, 12                                */
/*   menu="full" → Home / Mandatory tariffs / Transactions / Surveys / */
/*                 Notification engine / Manage / Resources            */
/*   menu="lite" → Resources / Contracts / Manage                      */
/* ------------------------------------------------------------------ */
function NavRow({ Icon, label, active, expanded, hasChildren }) {
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-[13px] font-body transition-colors ${
        active && !hasChildren
          ? 'bg-teal-600/10 text-teal-700 font-semibold'
          : active
          ? 'bg-teal-600 text-white font-semibold'
          : 'text-[#4B5563] hover:bg-[#F3F4F6]'
      }`}
    >
      <Icon size={15} className={active && !hasChildren ? 'text-teal-600' : active ? 'text-white' : 'text-[#6B7280]'} />
      <span className="flex-1">{label}</span>
      {hasChildren && (expanded
        ? <ChevronDown size={13} className={active ? 'text-white/80' : 'text-[#9CA3AF]'} />
        : <ChevronRight size={13} className="text-[#9CA3AF]" />)}
    </div>
  );
}

function SubNav({ label, active }) {
  return (
    <div
      className={`ml-9 px-3 py-1.5 rounded-md text-[12.5px] font-body cursor-pointer transition-colors ${
        active
          ? 'text-teal-700 font-semibold'
          : 'text-[#6B7280] hover:text-[#0A0A0A]'
      }`}
    >
      {label}
    </div>
  );
}

function SidebarV2({ menu = 'full', activeGroup, activeSub }) {
  const fullGroups = [
    { Icon: Home,           label: 'Home' },
    { Icon: Grid3x3,        label: 'Mandatory tariffs' },
    { Icon: Receipt,        label: 'Transactions' },
    { Icon: ClipboardList,  label: 'Surveys' },
    { Icon: BellIcon,       label: 'Notification engine' },
    { Icon: Users2,         label: 'Manage', subs: ['Roles', 'Users', 'Facilities', 'Organisations', 'Invitations'] },
    { Icon: FolderOpen,     label: 'Resources' },
  ];
  const liteGroups = [
    { Icon: FolderOpen,     label: 'Resources' },
    { Icon: FileIcon,       label: 'Contracts' },
    { Icon: Users2,         label: 'Manage', subs: ['Facility', 'User'] },
  ];
  const groups = menu === 'lite' ? liteGroups : fullGroups;

  return (
    <aside className="w-[240px] flex-shrink-0 flex flex-col bg-white border-r border-[#E5E7EB]">
      {/* Logo block */}
      <div className="px-5 py-5">
        <ShafafiyaLogo />
      </div>

      <nav className="flex-1 px-3 pt-1 pb-4 space-y-0.5 overflow-y-auto">
        {groups.map(g => {
          const isActive = g.label === activeGroup;
          const isExpanded = isActive && g.subs;
          return (
            <div key={g.label}>
              <NavRow Icon={g.Icon} label={g.label} active={isActive} expanded={!!isExpanded} hasChildren={!!g.subs} />
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-0.5 space-y-0"
                >
                  {g.subs.map(s => (
                    <SubNav key={s} label={s} active={s === activeSub} />
                  ))}
                </motion.div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer — Collapse / Settings / User */}
      <div className="border-t border-[#E5E7EB] px-3 py-3 space-y-1">
        <div className="flex items-center gap-2.5 px-3 py-1.5 text-[12px] font-body text-[#4B5563] hover:bg-[#F3F4F6] rounded-lg cursor-pointer">
          <ChevronsLeft size={13} /><span>Collapse menu</span>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-1.5 text-[12px] font-body text-[#4B5563] hover:bg-[#F3F4F6] rounded-lg cursor-pointer">
          <Settings size={13} /><span>Settings</span>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2 border-t border-[#E5E7EB] pt-3 mt-1">
          <div className="w-7 h-7 rounded-full bg-orange-400 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[9px] font-bold">OS</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11.5px] font-body font-semibold text-[#0A0A0A] truncate">Omar Saba</p>
            <p className="text-[10px] font-body text-[#6B7280] truncate">m@example.com</p>
          </div>
          <ChevronDown size={11} className="text-[#9CA3AF] flex-shrink-0" />
        </div>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/*  V2 Top Bar variants                                                */
/* ------------------------------------------------------------------ */
function TopBarSidebar({ showLogout = true }) {
  return (
    <div className="flex items-center justify-end gap-2.5 px-6 py-3 bg-white border-b border-[#E5E7EB] flex-shrink-0">
      {showLogout && (
        <button className="px-4 py-1.5 rounded-lg bg-teal-600 text-white text-[12px] font-body font-semibold hover:bg-teal-700">
          Logout
        </button>
      )}
      <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E7EB] text-[12px] font-body text-[#4B5563] hover:bg-[#F3F4F6]">
        <Globe size={13} className="text-[#6B7280]" />
        <span>English</span>
      </button>
      <button className="w-8 h-8 rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center hover:bg-[#F3F4F6]">
        <Bell size={14} className="text-[#4B5563]" />
      </button>
      <button className="w-8 h-8 rounded-full bg-teal-600/10 flex items-center justify-center">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-teal-700"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></svg>
      </button>
    </div>
  );
}

function TopBarHeaderOnly({ notification = false }) {
  return (
    <div className="flex items-center justify-between px-8 py-3.5 bg-white border-b border-[#E5E7EB] flex-shrink-0 relative">
      <ShafafiyaLogo size="sm" />
      <div className="flex items-center gap-2.5">
        <button className="relative w-9 h-9 rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center hover:bg-[#F3F4F6]">
          <Bell size={15} className="text-[#4B5563]" />
          {notification && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-teal-600" />}
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E7EB] text-[13px] font-body text-teal-700 hover:bg-[#F3F4F6]">
          <Globe size={13} className="text-teal-600" />
          <span>عربي</span>
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PortalShellV2                                                      */
/* ------------------------------------------------------------------ */
export default function PortalShellV2({
  children,
  variant = 'sidebar',           // 'sidebar' | 'headerOnly'
  menu = 'full',                  // 'full' | 'lite'  (when variant=sidebar)
  activeGroup = 'Manage',
  activeSub = 'Users',
  showLogout = true,
  overlay = null,
  fullOverlay = null,
  notification = false,
}) {
  const width = variant === 'headerOnly' ? 1240 : 1400;

  return (
    <div data-testid="portal-shell-v2" className="flex items-start justify-center h-full w-full overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="portal-shell"
        className="relative bg-white rounded-xl border border-black/5 shadow-2xl overflow-hidden flex"
        style={{ width: '100%', maxWidth: width, height: 680 }}
      >
        {variant === 'sidebar' && <SidebarV2 menu={menu} activeGroup={activeGroup} activeSub={activeSub} />}

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
          {variant === 'sidebar'
            ? <TopBarSidebar showLogout={showLogout} />
            : <TopBarHeaderOnly notification={notification} />}

          <div className="flex-1 overflow-y-auto relative min-h-0">
            <div className="min-h-full">
              {children}
            </div>
            {overlay}
          </div>
        </div>

        {fullOverlay}
      </motion.div>
    </div>
  );
}
